import React, { useState, useRef, useEffect } from 'react';
import { scaleLinear } from 'd3-scale';

type Primitive = string | number;

export interface D3HorizontalDatum {
  key: Primitive;
  value: number;
  color?: string;
  rank?: number;
}

interface D3HorizontalBarChartProps {
  data: D3HorizontalDatum[];
  width?: number;
  barHeight?: number;
  barPadding?: number;
  margin?: { top: number; right: number; bottom: number; left: number };
  title?: string;
  xLabel?: string;
  yLabel?: string;
  selectedKeys?: Primitive[];
  onBarClick?: (key: Primitive) => void;
  formatX?: (n: number) => string;
  formatKey?: (k: Primitive) => string;
  highlightTopN?: number;
}

interface TooltipData {
  key: Primitive;
  value: number;
  x: number;
  y: number;
}

export const D3HorizontalBarChart: React.FC<D3HorizontalBarChartProps> = ({
  data,
  width: propWidth,
  barHeight = 32,
  barPadding = 10,
  margin = { top: 20, right: 60, bottom: 40, left: 140 },
  title,
  xLabel,
  yLabel,
  selectedKeys = [],
  onBarClick,
  formatX = (n) => String(n),
  formatKey = (k) => String(k),
  highlightTopN = 0,
}) => {
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(500);
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    const updateWidth = () => {
      if (containerRef.current) {
        const computedStyle = window.getComputedStyle(containerRef.current);
        const paddingLeft = parseFloat(computedStyle.paddingLeft);
        const paddingRight = parseFloat(computedStyle.paddingRight);
        const width = containerRef.current.offsetWidth - paddingLeft - paddingRight;
        setContainerWidth(width);
      }
    };
    
    updateWidth();
    
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(containerRef.current);
    
    return () => resizeObserver.disconnect();
  }, []);
  
  if (!data?.length) return null;

  const width = propWidth || containerWidth;

  const isMobile = width < 500;
  const responsiveMargin = isMobile 
    ? { top: 20, right: 60, bottom: 40, left: 130 }
    : margin;
  
  const labelSpace = isMobile ? 50 : 60;
  const innerWidth = width - responsiveMargin.left - responsiveMargin.right - labelSpace;
  const height = data.length * (barHeight + barPadding) + responsiveMargin.top + responsiveMargin.bottom;
  const innerHeight = height - responsiveMargin.top - responsiveMargin.bottom;

  const hasNegative = data.some((d) => Number(d.value) < 0);
  const maxAbs = Math.max(...data.map((d) => Math.abs(Number(d.value) || 0)), 1);
  const maxValue = Math.max(...data.map((d) => Number(d.value) || 0), 1);
  
  const xScale = hasNegative 
    ? scaleLinear().domain([-maxAbs, maxAbs]).range([0, innerWidth])
    : isMobile 
      ? scaleLinear().domain([0, maxValue]).range([0, innerWidth])
      : scaleLinear().domain([0, maxValue]).range([0, innerWidth]).nice();
  
  const centerX = hasNegative ? xScale(0) : 0;

  return (
    <div ref={containerRef} className="amenity-chart-container" style={{ position: 'relative', width: '100%' }}>
      {title ? <h3>{title}</h3> : null}
      <div className="chart-scroll" style={{ overflowX: 'visible' }}>
        <svg width={width} height={height}>
          <g transform={`translate(${responsiveMargin.left}, ${responsiveMargin.top})`}>
            {hasNegative && (
              <line x1={centerX} x2={centerX} y1={0} y2={innerHeight} stroke="#4a5568" strokeWidth={2} opacity={0.8} />
            )}

            {data.map((d, i) => {
              const y = i * (barHeight + barPadding);
              const v = Number(d.value) || 0;
              const isPositive = v >= 0;
              const w = hasNegative ? Math.abs(xScale(v) - centerX) : xScale(v);
              const x = hasNegative ? (isPositive ? centerX : centerX - w) : 0;
              const isActive = selectedKeys.includes(d.key);
              const isFiltered = selectedKeys.length > 0 && !isActive;
              const isTopN = highlightTopN > 0 && i < highlightTopN;
              const fill = d.color || (hasNegative ? (isPositive ? '#27ae60' : '#e74c3c') : (isTopN ? '#FF5733' : '#2979FF'));
              const activeFill = isActive ? '#1d4ed8' : fill;
              return (
                <g key={String(d.key)}>
                  <rect
                    x={x}
                    y={y}
                    width={w}
                    height={barHeight}
                    fill={activeFill}
                    opacity={isFiltered ? 0.35 : 0.9}
                    stroke="#2d3748"
                    strokeWidth={1}
                    rx={2}
                    style={{ 
                      cursor: onBarClick ? 'pointer' : 'default', 
                      transition: 'x 400ms, width 400ms, opacity 200ms, fill 200ms, filter 200ms, transform 200ms',
                      transformOrigin: `${x + w/2}px ${y + barHeight/2}px`
                    }}
                    onClick={() => onBarClick && onBarClick(d.key)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.filter = 'brightness(1.2)';
                      e.currentTarget.style.transform = 'scale(1.01)';
                      const rect = e.currentTarget.getBoundingClientRect();
                      setTooltip({
                        key: d.key,
                        value: v,
                        x: rect.left + rect.width / 2,
                        y: rect.top,
                      });
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.filter = 'brightness(1)';
                      e.currentTarget.style.transform = 'scale(1)';
                      setTooltip(null);
                    }}
                  />

                  <text
                    x={-10}
                    y={y + barHeight / 2}
                    textAnchor="end"
                    dy="0.35em"
                    fontSize={isMobile ? 12 : 14}
                    fill="#a0aec0"
                    fontWeight={500}
                    style={{ pointerEvents: 'none' }}
                  >
                    {formatKey(d.key)}
                  </text>

                  <text
                    x={hasNegative ? (isPositive ? x + w + 6 : x - 6) : x + w + 6}
                    y={y + barHeight / 2}
                    textAnchor={hasNegative ? (isPositive ? 'start' : 'end') : 'start'}
                    dy="0.35em"
                    fontSize={isMobile ? 10 : 11}
                    fill="#e2e8f0"
                    fontWeight={600}
                    style={{ pointerEvents: 'none' }}
                  >
                    {hasNegative && v >= 0 ? '+' : ''}{formatX(v)}
                  </text>
                </g>
              );
            })}

            <g transform={`translate(0, ${innerHeight})`}>
              <line x1={0} x2={innerWidth} stroke="#4a5568" strokeWidth={1} />
              {xScale.ticks(6).map((tick) => (
                <g key={tick} transform={`translate(${xScale(tick)}, 0)`}>
                  <line y2={6} stroke="#4a5568" />
                  <text y={18} textAnchor="middle" fontSize={12} fill="#a0aec0" fontWeight={500}>
                    {formatX(tick)}
                  </text>
                </g>
              ))}
              {xLabel ? (
                <text x={innerWidth / 2} y={34} textAnchor="middle" fontSize={14} fill="#e2e8f0" fontWeight={600}>
                  {xLabel}
                </text>
              ) : null}
            </g>
          </g>
        </svg>
      </div>
      
      {tooltip && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y - 10,
            transform: 'translate(-50%, -100%)',
            background: 'rgba(30, 41, 59, 0.95)',
            color: '#e2e8f0',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 500,
            pointerEvents: 'none',
            zIndex: 1000,
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(71, 85, 105, 0.5)',
            lineHeight: '1.5',
          }}
        >
          <div>{yLabel || 'Categoria'}: {formatKey(tooltip.key)}</div>
          <div>{xLabel || 'Valor'}: {hasNegative && tooltip.value >= 0 ? '+' : ''}{formatX(tooltip.value)}</div>
        </div>
      )}
    </div>
  );
};


